<template>
  <div>
    <v-row justify="center" class="py-15" no-gutters v-if="loading">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>
    <template v-else>
      <div class="mt-6" v-for="company in companies">
        <h2>{{ toLatin(company.name) }}</h2>
        <v-data-table
          :headers="headers"
          :items="customSearch(company.meals, headers, search)"
          :loading="loading"
          class="striped-table ordera-box-shadow"
          hide-default-footer
          disable-pagination
          dense
        >
          <template v-slot:item.num="{ item, index }">
            {{ index + 1 }}
          </template>
          <template v-slot:item.shift1="{ item }">
            <span v-html="item.shift1.map((item) => item.text).join(', ')"></span>
          </template>
          <template v-slot:item.shift2="{ item }">
            <span v-html="item.shift2.map((item) => item.text).join(', ')"></span>
          </template>
          <template v-slot:item.shift3="{ item }">
            <span v-html="item.shift3.map((item) => item.text).join(', ')"></span>
          </template>
          <template v-slot:item.shift4="{ item }">
            <span v-html="item.shift4.map((item) => item.text).join(', ')"></span>
          </template>
          <template v-slot:body.append>
            <tr class="font-weight-bold">
              <td></td>
              <td>{{ $t("total") }}</td>
              <td>{{ company.totals.shift1 }}</td>
              <td>{{ company.totals.shift2 }}</td>
              <td>{{ company.totals.shift3 }}</td>
              <td>{{ company.totals.shift4 }}</td>
              <td>{{ company.totals.total }}</td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </template>
  </div>
</template>
<script>
import reportService from "@/services/report";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyReportTabMeals",
  mixins: [hasPrinting, hasExcel, hasReportPrinting],
  data() {
    return {
      report: null,
      loading: false,
    };
  },
  created() {
    this.fetchUserlist();
  },
  methods: {
    fetchUserlist() {
      this.loading = true;
      reportService
        .getCompanyUserlist({
          company_id: this.selectedCompanies,
          date: this.dates.from,
          date2: this.dates.to,
        })
        .then((response) => {
          this.report = response.data;
        })
        .catch((error) => {
          console.error("Error fetching meals:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  computed: {
    companies() {
      if (!this.report) {
        return [];
      }

      const groupAndFormatUsers = (users) => {
        if (!users || users.length === 0) {
          return [];
        }
        const userMap = users.reduce((acc, user) => {
          const existingUser = acc.get(user.id);
          if (existingUser) {
            existingUser.quantity += user.quantity;
          } else {
            acc.set(user.id, {
              ...user,
              name: this.toLatin(user.name),
              lastname: this.toLatin(user.lastname),
            });
          }
          return acc;
        }, new Map());

        return Array.from(userMap.values()).map((user) => {
          return {
            name: `${this.toLatin(user.name)} ${this.toLatin(user.lastname)}`,
            quantity: user.quantity,
            text: `<span class="font-weight-bold">${user.quantity}x</span> ${this.toLatin(
              user.name
            )} ${this.toLatin(user.lastname)}`,
          };
        });
      };

      return Object.entries(this.report).map(([companyName, companyData]) => {
        const totals = {
          shift1: 0,
          shift2: 0,
          shift3: 0,
          shift4: 0,
          total: 0,
        };
        const meals = (companyData.meals || [])
          .filter((meal) => meal.total > 0 && meal.id)
          .map((meal) => {
            const usersByShift = meal.users || {};

            const shift1Users = usersByShift["1-smena"] || [];
            const shift2Users = usersByShift["2-smena"] || [];
            const shift3Users = usersByShift["3-smena"] || [];
            const shift4Users = usersByShift["4-smena"] || [];

            totals.shift1 += shift1Users.reduce((sum, u) => sum + u.quantity, 0);
            totals.shift2 += shift2Users.reduce((sum, u) => sum + u.quantity, 0);
            totals.shift3 += shift3Users.reduce((sum, u) => sum + u.quantity, 0);
            totals.shift4 += shift4Users.reduce((sum, u) => sum + u.quantity, 0);
            totals.total += meal.total;

            return {
              ...meal,
              shift1: groupAndFormatUsers(shift1Users),
              shift2: groupAndFormatUsers(shift2Users),
              shift3: groupAndFormatUsers(shift3Users),
              shift4: groupAndFormatUsers(shift4Users),
            };
          });

        return {
          name: this.toLatin(companyName),
          meals,
          totals,
        };
      });
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies.map((company) => company.id);
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
    restaurantId() {
      return this.$store.state.company?.restaurant_id || this.$store.state.company;
    },
    search() {
      return this.$store.state.report.filters.search;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", print: false },
        { text: this.$t("meals"), value: "name" },
        { text: this.$t("shift_one"), value: "shift1" },
        { text: this.$t("shift_two"), value: "shift2" },
        { text: this.$t("shift_three"), value: "shift3" },
        { text: this.$t("shift_four"), value: "shift4" },
        { text: this.$t("total"), value: "total" },
      ];
    },
  },
  watch: {
    "dates.from"(newVal, oldVal) {
      this.fetchUserlist();
    },
    selectedCompanies(newVal, oldVal) {
      this.fetchUserlist();
    },
    isPrinting(newVal, oldVal) {
      if (newVal) {
        this.printDocument({
          tables: this.companies.map((company) => {
            return {
              headers: this.headers.filter((h) => h.print !== false),
              items: this.customSearch(company.meals, this.headers, this.search).map(
                (meal) => ({
                  ...meal,
                  shift1: meal.shift1.map((user) => user.text).join(", "),
                  shift2: meal.shift2.map((user) => user.text).join(", "),
                  shift3: meal.shift3.map((user) => user.text).join(", "),
                  shift4: meal.shift4.map((user) => user.text).join(", "),
                  company: this.toLatin(company.name),
                })
              ),
              title: this.toLatin(company.name) + " " + this.subtitle,
              showCaption: true,
            };
          }),
          title: [this.$t("report"), this.$t("meal_worker").toLowerCase()].join(": "),
          hideDatetime: true,
          additionalStyles: `
              .print-table {
                page-break-after: always;
              }
              .print-table:last-child {
                page-break-after: auto;
              }
            `,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName:
            [this.$t("report"), this.$t("meal_worker")].join(" ").toLowerCase() + ".xlsx",
          sheets: this.companies.map((company) => {
            return {
              name: this.toLatin(company.name),
              headers: this.headers.filter((h) => h.print !== false),
              items: this.customSearch(company.meals, this.headers, this.search).map(
                (meal) => ({
                  ...meal,
                  shift1: meal.shift1
                    .map((user) => [user.quantity, user.name].join("x "))
                    .join(", "),
                  shift2: meal.shift2
                    .map((user) => [user.quantity, user.name].join("x "))
                    .join(", "),
                  shift3: meal.shift3
                    .map((user) => [user.quantity, user.name].join("x "))
                    .join(", "),
                  shift4: meal.shift4
                    .map((user) => [user.quantity, user.name].join("x "))
                    .join(", "),
                  company: this.toLatin(company.name),
                })
              ),
              title: this.toLatin(company.name),
              subtitle: this.subtitle,
            };
          }),
        });
      }
    },
  },
};
</script>
